import React, { useEffect, useState } from 'react';
import { Alert, Box, Card, CardContent, CircularProgress, Stack, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AnalysisTabBar from '../components/AnalysisTabBar';
import { useAuth } from '../contexts/AuthContext';
import { reportLanguageFromPreference } from '../i18n/inlineTranslations';

const API_BASE_URL = process.env.REACT_APP_STUDIO_API_URL || 'http://localhost:4173/api/studio/v1';

const ChartRemedies: React.FC = () => {
  const { id = '' } = useParams();
  const { i18n } = useTranslation();
  const { state: authState } = useAuth();
  const [report, setReport] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const language = reportLanguageFromPreference(i18n.resolvedLanguage || i18n.language);

  useEffect(() => {
    setReport(null);
    if (!id || !authState.token) return;
    const controller = new AbortController();
    setLoading(true);
    fetch(`${API_BASE_URL}/charts/${id}/remedies?language=${encodeURIComponent(language)}`, {
      headers: { Authorization: `Bearer ${authState.token}` }, signal: controller.signal,
    }).then(async (response) => {
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.message || payload.error || 'Unable to load remedies.');
      setReport(payload.data);
      setError('');
    }).catch((requestError) => {
      if (requestError.name !== 'AbortError') setError(requestError.message || 'Unable to load remedies.');
    }).finally(() => setLoading(false));
    return () => controller.abort();
  }, [authState.token, id, language]);

  return <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 1.5, md: 3 }, py: 2 }}>
    <AnalysisTabBar chartId={id} active="remedies" />
    {loading ? <Stack alignItems="center" py={8}><CircularProgress /></Stack> : null}
    {error ? <Alert severity="error">{error}</Alert> : null}
    {report ? <>
      <Card sx={{ mb: 2, background: "linear-gradient(135deg, var(--app-surface-neutral, #f0fdf4), var(--app-surface-orange, #fff7ed))" }}><CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
          <Box><Typography variant="h4" fontWeight={900}>{report.title}</Typography><Typography color="text.secondary">{report.subtitle}</Typography></Box>
        </Stack>
        <Typography mt={2}>{report.basis}</Typography>
      </CardContent></Card>
      <Stack spacing={2}>{report.priorities.map((priority: any) => <Card key={priority.planet} variant="outlined"><CardContent>
        <Typography variant="h5" fontWeight={900} mb={1.5}>{priority.planetLabel}</Typography>
        <Stack spacing={1.25}>{priority.categories.map((category: any) => <Box key={category.id} sx={{ p: 1.5, borderRadius: 2, bgcolor: 'action.hover' }}>
          <Typography fontWeight={800}>{category.title}</Typography>
          {category.items.map((item: string) => <Typography key={item} mt={0.5}>{item}</Typography>)}
        </Box>)}</Stack>
      </CardContent></Card>)}</Stack>
      <Alert severity="info" sx={{ mt: 2 }}>{report.reviewGuidance}</Alert>
      {report.crystalGuide ? <Box mt={3} dir={report.crystalGuide.language === 'ur' ? 'rtl' : 'ltr'}>
        <Typography variant="h4">{report.crystalGuide.title}</Typography>
        <Typography my={1}>{report.crystalGuide.intro}</Typography>
        <Typography my={1}>{report.crystalGuide.assessment}</Typography>
        {report.crystalGuide.missing.map((item: string) => <Typography key={item}>{item}</Typography>)}
        {report.crystalGuide.sections.map((group: any) => <Box key={group.id} mt={3}>
          <Typography variant="h5">{group.title}</Typography>
          {group.cards.map((card: any) => <Card key={card.id} variant="outlined" sx={{ my: 1 }}><CardContent>
            <Typography variant="h6">{card.title}</Typography>
            <Typography color="text.secondary" my={1}>{card.why}</Typography>
            <Typography>{card.meaning}</Typography>
            <Typography fontWeight={700} mt={1}>{report.crystalGuide.labels.optionalCrystal}: {card.crystals.join(', ')}</Typography>
            <Typography>{card.practice}</Typography>
          </CardContent></Card>)}
        </Box>)}
        {report.crystalGuide.general.map((item: any) => <Box key={item.id} mt={2}><Typography variant="h6">{item.title}</Typography><Typography>{item.body}</Typography></Box>)}
      </Box> : null}
      <Typography variant="caption" color="text.secondary" display="block" mt={2}>{report.disclaimer}</Typography>
    </> : null}
  </Box>;
};

export default ChartRemedies;
